"use client";

import { SiOpenai } from "@icons-pack/react-simple-icons";
import { UserRound } from "lucide-react";
import { useState } from "react";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { TijanaAvatar } from "~/components/tijana/avatar";
import { cn } from "~/lib/utils";

const COMPARISONS = [
  {
    key: "odluka",
    label: "Velika odluka",
    prompt:
      "Ne znam da li da dam otkaz. Svako jutro mi je muka pre posla, ali me je strah da pogrešim.",
    chatGpt:
      "To je velika odluka. Razmisli koliko dugo se tako osećaš, imaš li finansijsku rezervu i da li postoji mogućnost promene tima ili uloge. Može pomoći i lista razloga za ostanak i odlazak.",
    tijana:
      "Ne moramo još da odlučimo. Kada pomisliš da ostaješ još šest meseci, šta ti prvo stegne stomak: sam posao, odnos sa ljudima ili osećaj da nemaš izlaz?",
  },
  {
    key: "tezak-dan",
    label: "Težak dan",
    prompt: "Ceo dan sam među ljudima, a osećam se potpuno sama.",
    chatGpt:
      "Usamljenost može postojati i kada si okružena ljudima. Probaj da se javiš osobi kojoj veruješ, napišeš kako se osećaš ili pronađeš prostor u kom možeš da budeš otvorenija.",
    tijana:
      "Znači da ljudi jesu tu, ali nema mesta gde možeš da budeš stvarno viđena. Sa kim si danas najviše želela da budeš iskrena, a nisi?",
  },
  {
    key: "svadja",
    label: "Posle svađe",
    prompt:
      "Posvađali smo se i sad hoću da mu pošaljem poruku koju ću verovatno zažaliti.",
    chatGpt:
      "Sačekaj da se emocije malo smire i prvo napiši poruku u beleške. Kada budeš spremna, koristi rečenice koje počinju sa „ja“ i izbegni optuživanje.",
    tijana:
      "Nemoj još da je šalješ. Šta želiš da ta poruka uradi: da ga povredi kao što je on povredio tebe, da postavi granicu ili da ponovo otvori razgovor?",
  },
] as const;

type Comparison = (typeof COMPARISONS)[number];

function UserMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-end gap-2">
      <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-brand-action px-4 py-3 text-sm leading-relaxed text-white">
        {children}
      </div>
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="bg-secondary text-brand">
          <UserRound className="h-4 w-4" aria-hidden="true" />
        </AvatarFallback>
      </Avatar>
    </div>
  );
}

function ComparisonWindow({
  product,
  comparison,
}: {
  product: "chatgpt" | "tijana";
  comparison: Comparison;
}) {
  const isTijana = product === "tijana";
  const answer = isTijana ? comparison.tijana : comparison.chatGpt;

  return (
    <article className="overflow-hidden rounded-xl border border-white/20 bg-background text-foreground">
      <div className="flex items-center justify-between border-b bg-secondary px-4 py-3">
        <div className="flex items-center gap-2.5">
          {isTijana ? (
            <TijanaAvatar size={28} rounded="full" />
          ) : (
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-background">
              <SiOpenai size={16} aria-hidden="true" />
            </span>
          )}
          <h3 className="text-sm font-semibold">
            {isTijana ? "Tijana" : "ChatGPT"}
          </h3>
        </div>
        <span className="text-[11px] text-muted-foreground">Ilustracija</span>
      </div>

      <div className="min-h-80 space-y-6 p-4 sm:p-5">
        <UserMessage>{comparison.prompt}</UserMessage>

        <div className="flex items-start gap-2">
          {isTijana ? (
            <TijanaAvatar size={32} rounded="full" />
          ) : (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
              <SiOpenai size={18} aria-hidden="true" />
            </span>
          )}
          <div
            className={cn(
              "max-w-[85%] rounded-2xl rounded-tl-md border px-4 py-3 text-sm leading-relaxed",
              isTijana ? "bg-secondary" : "bg-muted/40 text-muted-foreground",
            )}
          >
            {answer}
          </div>
        </div>
      </div>
    </article>
  );
}

export function TijanaVsChatGptSection() {
  const [selectedKey, setSelectedKey] = useState<Comparison["key"]>(
    COMPARISONS[0].key,
  );
  const comparison =
    COMPARISONS.find((item) => item.key === selectedKey) ?? COMPARISONS[0];

  return (
    <section className="bg-brand-forest text-white">
      <div className="container px-4 py-14 md:px-8 md:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/65">
              Tijana ili ChatGPT?
            </p>
            <h2 className="marketing-heading mt-3 text-3xl leading-tight text-balance sm:text-4xl">
              Isto pitanje ChatGPT-u i Tijani.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-white/70">
              Oba odgovora mogu biti korisna. Razlika je u tome šta se dešava
              posle prvog odgovora.
            </p>
          </div>

          <div
            className="mt-8 flex flex-wrap gap-2"
            aria-label="Izaberi situaciju za poređenje"
          >
            {COMPARISONS.map((item) => {
              const isSelected = item.key === comparison.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => setSelectedKey(item.key)}
                  className={cn(
                    "rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
                    isSelected
                      ? "border-background bg-background text-foreground"
                      : "border-white/25 text-white hover:border-white/50",
                  )}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <ComparisonWindow product="chatgpt" comparison={comparison} />
            <ComparisonWindow product="tijana" comparison={comparison} />
          </div>

          <p className="mt-5 text-xs text-white/50">
            Odgovori su ilustrativni i ne predstavljaju snimak stvarnog
            razgovora.
          </p>
        </div>
      </div>
    </section>
  );
}
